/**
 * useRealtime — single SSE connection to the backend realtime stream.
 *
 * Mount ONCE near the app root. Flow: mint a single-use ticket → open an
 * EventSource at /api/v1/realtime/events?ticket=... → on each push event,
 * invalidate server-state (TanStack Query) and record it in the realtime
 * store.
 *
 * Reconnection is MANUAL on purpose: native EventSource auto-retries the same
 * URL, but our ticket is single-use, so a stale ticket would 401-loop.
 * Instead we close on error and reconnect with a FRESH ticket + exponential
 * backoff. StrictMode-safe via per-effect closure state (no shared refs).
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/lib/api';
import { getToken } from '@/lib/auth';
import { useLookAlong } from '@/lib/impersonation';
import { routeRealtimeEvent } from '@/hooks/realtimeRouting';
import { toast } from '@/hooks/useToast';
import { useRealtimeStore } from '@/stores/realtime';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

// Server-push event types we listen for. data_refreshed = nightly tracer;
// mapping_* = live Bronnen-progress (see realtimeRouting.ts).
const EVENT_TYPES = ['data_refreshed', 'mapping_progress', 'mapping_completed'] as const;

const MAX_BACKOFF_MS = 30_000;

export function useRealtime(): void {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  // The stream is opened with whatever token this tab holds, and the backend
  // keeps it open until the client disconnects -- there is no lifetime cap.
  // An already-open EventSource does not care that the look-along token went
  // away, so without this dependency the customer's events would keep arriving
  // for as long as the tab stays open, long after the thirty minutes. Ending a
  // look-along changes this value, the effect tears down, the stream closes.
  const lookAlong = useLookAlong();

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let retry = 0;

    // Coalesce bursty mapping_progress events → at most one retailers refetch
    // every 2s, so a large run doesn't trigger a refetch storm. The review-
    // queue count (sidebar badge) rides along: it grows pair by pair and this
    // is what replaced its 30 s poll.
    let progressTimer: ReturnType<typeof setTimeout> | null = null;
    const invalidateRetailersThrottled = () => {
      if (progressTimer) return;
      progressTimer = setTimeout(() => {
        progressTimer = null;
        void queryClient.invalidateQueries({ queryKey: ['retailers'] });
        void queryClient.invalidateQueries({ queryKey: ['mapping-review'] });
      }, 2_000);
    };

    const { setStatus, markEvent } = useRealtimeStore.getState();

    const handler = (e: MessageEvent) => {
      let ts = new Date().toISOString();
      let type = e.type;
      let payload: Record<string, unknown> = {};
      try {
        const env = JSON.parse(e.data);
        if (typeof env?.ts === 'string') ts = env.ts;
        if (typeof env?.type === 'string') type = env.type;
        if (env?.payload && typeof env.payload === 'object') payload = env.payload;
      } catch {
        // keep defaults — malformed frame shouldn't break the handler
      }
      markEvent(type, ts);

      const action = routeRealtimeEvent(type, payload);
      switch (action.invalidate) {
        case 'all':
          void queryClient.invalidateQueries();
          break;
        case 'retailers-throttled':
          invalidateRetailersThrottled();
          break;
        case 'mapping-done':
          void queryClient.invalidateQueries({ queryKey: ['retailers'] });
          void queryClient.invalidateQueries({ queryKey: ['mappings'] });
          // Finished mapping changes the review-queue count and the dashboard
          // totals (skus mapped / per-category) — these used to poll for it.
          void queryClient.invalidateQueries({ queryKey: ['mapping-review'] });
          void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          break;
        case 'ai-pending':
          void queryClient.invalidateQueries({ queryKey: ['connect-pending'] });
          break;
      }
      if (action.toast) {
        toast[action.toast.variant](i18n.t(action.toast.titleKey, action.toast.vars));
      }
    };

    async function connect() {
      if (cancelled) return;
      // No token = not authenticated. Don't poll realtime/ticket (it would 401);
      // AppShell only mounts when authenticated, but the token can be cleared
      // mid-session (expiry/logout) while this effect is still alive — bail
      // cleanly instead of spamming 401s.
      if (!getToken()) {
        setStatus('closed');
        return;
      }
      setStatus(retry === 0 ? 'connecting' : 'reconnecting');

      let ticket: string;
      try {
        ticket = (await api.realtime.ticket()).ticket;
      } catch {
        scheduleReconnect();
        return;
      }
      if (cancelled) return;

      const source = new EventSource(
        `${BASE}/api/v1/realtime/events?ticket=${encodeURIComponent(ticket)}`,
      );
      es = source;
      source.onopen = () => {
        retry = 0;
        setStatus('open');
      };
      EVENT_TYPES.forEach((t) => source.addEventListener(t, handler as EventListener));
      source.onerror = () => {
        source.close();
        if (es === source) es = null;
        scheduleReconnect();
      };
    }

    function scheduleReconnect() {
      if (cancelled) return;
      setStatus('reconnecting');
      const delay = Math.min(1000 * 2 ** retry, MAX_BACKOFF_MS);
      retry += 1;
      timer = setTimeout(connect, delay);
    }

    void connect();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (progressTimer) clearTimeout(progressTimer);
      if (es) es.close();
      setStatus('closed');
    };
  }, [queryClient, i18n, lookAlong?.token]);
}
